<template>
  <div v-rect-loading="loading" class="edit-major-container">
    <div class="major-editor-header">
      <div class="major-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="major-info-container">
      <WrappedFrom
        ref="form"
        class="major-info"
        label-width="180px"
        :model="majorInfo"
        :formLines="formLines"
      >
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" type="primary" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { addI18nProperty } from '@/i18n'
import { mixedRouterPush } from '@/router/routes'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.majorManagement',
      loading: false,
      majorListOptions: [],
      approvedMajorList: [],
      majorInfo: {
        target_major_tag_id: null,
        en_tag: null,
        tag: null,
        review_status: false
      },
      approvedMajor: {
        data: null,
        currentData: null,
        page: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    tagId () {
      return this.$route.params.id
    },
    isEdit () {
      return this.$route.meta.isEdit
    },
    titleLabel () {
      if (this.isEdit) {
        return this.$t(`${this.i18nPrefix}.label.editMajor`)
      }
      return this.$t(`${this.i18nPrefix}.label.addMajor`)
    },
    formLines () {
      const vm = this
      const lines = {
        target_major_tag_id: {
          elType: 'el-select',
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.keywords`),
          label: this.$t(`${this.i18nPrefix}.label.mergeTo`),
          hidden: !this.isEdit,
          option: {
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: {
            clearable: true,
            noDataText: this.$t(`${this.i18nPrefix}.placeholder.pleaseStartTyping`)
          },
          filterMethod: (keywords) => {
            const approvedMajor = vm.approvedMajor
            approvedMajor.page = 1
            let filteredList = []
            if (keywords) {
              const keywordsUpper = _.toUpper(keywords)
              filteredList = _.filter(vm.approvedMajorList, (item) => _.includes(item.search, keywordsUpper))
            }
            approvedMajor.currentData = filteredList
            approvedMajor.data = filteredList.slice(0, approvedMajor.pageSize)
            return approvedMajor.data
          },
          events: {
            change: this.onMajorSelect,
            'visible-change': (visible) => {
              if (!visible) {
                vm.approvedMajor.currentData = []
              }
            }
          },
          loadMore: (filter) => {
            const approvedMajor = vm.approvedMajor
            if (approvedMajor.currentData && approvedMajor.page * approvedMajor.pageSize > approvedMajor.currentData.length) {
              return
            }
            const data = approvedMajor.currentData.slice(approvedMajor.page * approvedMajor.pageSize, (approvedMajor.page + 1) * approvedMajor.pageSize)
            approvedMajor.page += 1
            filter.option.data.push(...data)
          }
        },
        tag: {
          label: this.$t(`${this.i18nPrefix}.label.majorChineseName`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        en_tag: {
          label: this.$t(`${this.i18nPrefix}.label.majorEnglishName`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        review_status: {
          elType: 'el-checkbox',
          label: this.$t(`${this.i18nPrefix}.label.reviewStatus`)
        }
      }
      return lines
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('majorManagement', ['getMajorManagementList', 'createMajor', 'updateMajor']),
    ...mapActions('application', ['getMajorList']),
    async initPage () {
      this.loading = true
      try {
        await Promise.all([
          this.loadMajorTagInfo(),
          this.getApprovedMajorList()
        ])
      } finally {
        this.loading = false
      }
    },
    async getFormRef () {
      if (!this.$refs.form) {
        await this.$nextTick()
      }
      return this.$refs.form
    },
    async getApprovedMajorList () {
      if (!this.tagId) {
        return
      }
      const result = await this.getMajorList()
      const formatMajor = _.chain(result.majors)
        .filter(item => (item.cn_name?.trim() && item.name?.trim()))
        .map(item => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'name')
          item.search = _.toUpper(`${item.cn_name}|${item.name}`)
          return item
        })
        .value()
      this.approvedMajorList = formatMajor
    },
    async loadMajorTagInfo () {
      if (!this.tagId) {
        return
      }

      const resp = await this.getMajorManagementList({ tag_id: this.tagId })
      if (resp) {
        this.majorInfo = resp
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async handleSave () {
      const form = await this.getFormRef()
      const isValid = await form.submit()
      if (!isValid) {
        return null
      }
      this.loading = true
      let resp = null
      try {
        if (this.tagId) {
          const params = { ...this.majorInfo }
          params.tag_id = this.tagId
          resp = await this.updateMajor(params)
        } else {
          resp = await this.createMajor(this.majorInfo)
        }
        if (resp) {
          this.$message({
            type: 'success',
            message: this.$t('common.message.saveSuccess')
          })
          this.handleCancel()
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-major-container {
  .major-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;
    .major-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }
  .major-info-container {
    width: 600px;
    margin: 30px auto 0;
    .major-info {}
    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;

        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
